
type role = {
  all?: boolean
  read?: boolean
  create?: boolean
  update?: boolean
  delete?: boolean
  publish?: boolean
  sub_acl : {
    read?: boolean
    create?: boolean
    update?: boolean
    delete?: boolean
    publish?: boolean
  }
}

export declare interface Asset {
  ACL: {
    roles: role[],
    [key: string]: any
  }
  url: string
  filename: string
  uid: string
  [k: string]: any
}

/**
 * AssetType map, the AssetPicker component expects the "only"
 * prop to be one of the keys. The API uses the corresponding value
 */
export const AssetType = {
  image: 'images',
  video: 'videos',
  audio: 'audio',
  document: 'documents',
  pdfs: 'pdfs',
  presentations: 'presentations',
  spreadsheets: 'spreadsheets',
  'plain-text': 'plain-text',
  code: 'code',
  executable: 'executable',
  archive: 'archive'
} as const

export type GetUsers = () => Promise<Record<string, string>>

export type GetAssets = (args: GetAssetsOptions) => Promise<{ assets?: any[]; count?: number; [k: string]: any }>

export type GetAssetsOptions = {
  folderUid: string
  limit?: number
  skip?: number
  query?: string
  sort?: {
    id: string
    order: 'asc' | 'desc'
  }
  excludeAssetUids?: string[]
  types?: (keyof typeof AssetType)[]
  [key: string]: any // Allow for additional vendor-specific parameters
}

export type GetFolders = (args: GetFoldersOptions) => Promise<{ assets?: any[]; count?: number; [k: string]: any }>

export type GetFoldersOptions = Omit<GetAssetsOptions, 'excludeAssetUids' | 'types'>

export type CreateFolder = (args: { folderName: string; parentUid: string }) => Promise<any>

export type UploadFiles = (args: {
  files: any[]
  options: {
    parentFolderUid: string
  }
}) => Promise<any>

export interface AssetModalProps {
  /**
   * Function called when the user closes the modal
   * @returns void
   */
  onCancel?: () => void
  /**
   * Function called to close the modal
   * @returns void
   */
  closeModal?: () => void
  /**
   * Determines if multiple selections are allowed
   */
  multiple?:
    | boolean
    | {
        /**
         * Determines the maximum number of selections allowed
         */
        max: number
      }
  /**
   * Determines the type of assets allowed.
   * This is passed to `getAssets` as `types` property which is in an array.
   *
   * Note - CS Assets API accepts the asset `types` property as an array.
   * Asset picker always accepted one type, so `only` prop remains a string
   * for backward compatibility
   */
  only?: keyof typeof AssetType
  /**
   * Comma-separated string which determines file extensions allowed
   */
  fileTypes?: string
  /**
   * Determines the minimum and maximum fle size
   */
  size?: {
    min?: number
    max?: number
  },
  /**
 * Determines the display mode for assets within the modal.
 * Can be either a 'list' or 'grid' view.
 */
  displayType?: DisplayType;
  /**
 * Callback function triggered when the display type is changed.
 * @param displayType - The new display type selected by the user
 */
  onDisplayTypeChange?: (displayType: DisplayType) => void;
  /**
   * Determines if the modal is in view-only mode
   */
  viewOnly?: boolean;
  /**
   * Determines if the modal is from trash page
   */
  fromTrashPage?: boolean;
  /**
   * Function called when the user restores the assets without assets
   */
  handleRestoreWithoutAssets?: () => void;
  /**
   * Function called when the user restores the assets with assets
   */
  handleRestoreWithAssets?: () => void;
}

export type DisplayType = 'list' | 'grid';
